import React, { useState } from 'react'

import * as Yup from 'yup'
import I18n from 'i18n'
import { t } from 'i18n'
import { useNotify } from 'utils/useNotify'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { IconButton } from '@material-ui/core'
import { Form, Formik, FormikHelpers } from 'formik'
import { CheckboxField, TextField, TranslatedItemSelectField, TranslatedSelectField } from 'components/formik'
import Button from 'components/button'
import { vatNumber } from 'utils'
import { COUNTRIES } from 'utils/countries'
import { languages } from 'utils/languages'
import { commonStyles, fetchJson, SignUp, useSignUp } from './common'
import Loading from 'components/loading'
import Tooltip from 'components/tooltip'

const LAW_FIRM_SIZES: readonly { readonly code: string; readonly name: string }[] = ['1-5', '6-15', '16-30', '31-100', '101-200', '200+'].map(
  (size) => ({
    code: size,
    name: size
  })
)

const useStyles = makeStyles((theme) => ({
  ...commonStyles(theme),
  tooltipContainer: {
    position: 'relative',
    height: 0
  },
  tooltip: {
    position: 'absolute',
    right: 0,
    top: theme.spacing(-1),
    zIndex: 1,
    padding: theme.spacing(0.15)
  },
  textRight: {
    '& .MuiInputBase-input ': {
      textAlign: 'right'
    }
  },
  checkbox: {
    marginTop: -theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiFormControlLabel-label': {
      fontWeight: theme.palette.inputs.label.semibold,
      color: theme.palette.common.black,
      fontSize: theme.typography.caption.fontSize
    }
  }
}))

const validationSchema = Yup.object({
  subdomain: Yup.string().required(),
  lawFirmName: Yup.string().required(),
  lawFirmRegistrationNumber: Yup.string().nullable(),
  lawFirmIsVatPayer: Yup.boolean(),
  lawFirmVatRegistrationNumber: vatNumber.nullable().when('lawFirmIsVatPayer', {
    is: true,
    then: (schema) => schema.required()
  }),
  lawFirmCountry: Yup.object().required(),
  lawFirmAddress: Yup.string().required(),
  lawFirmPostalCode: Yup.string().required(),
  roleInLawFirm: Yup.string().nullable(),
  lawFirmSize: Yup.object().nullable(),
  practiceArea: Yup.string().nullable(),
  systemLocale: Yup.string().required(),
  timeZoneName: Yup.object().nullable()
})

interface CompanyFormProps {
  subdomain?: string
  lawFirmName?: string
  lawFirmRegistrationNumber?: string
  lawFirmIsVatPayer: boolean
  lawFirmVatRegistrationNumber?: string
  lawFirmCountry?: SelectValue
  lawFirmAddress?: string
  lawFirmPostalCode?: string
  roleInLawFirm?: string
  lawFirmSize?: SelectValue
  practiceArea?: string
  systemLocale?: string
  timeZoneName?: SelectValue
}

interface SelectValue {
  id?: string
  name?: string
}

const findSelectItemInCodifier = (codifier: readonly { readonly code: string; readonly name: string }[], codifierCode: string | undefined) => {
  let selectItem: SelectValue = {}
  if (codifierCode) {
    const item = codifier.find((i) => i.code === codifierCode)
    if (item) {
      selectItem = {
        id: item.code,
        name: item.name
      }
    }
  }
  return selectItem
}

const formatSubdomain = (subdomain: string) => {
  return transliterate(subdomain || '')
    .toLowerCase()
    .replace(/\s/g, '-')
    .replace(/[^a-z0-9\-]/g, '')
}

// https://stackoverflow.com/a/37511463/1932073
const transliterate = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '')

interface TimeZoneData {
  default: string
  zones: [{ code: string; name: string }]
}

const fetchTimeZoneData = async (setTimeZoneData: React.Dispatch<React.SetStateAction<TimeZoneData>>) => {
  const timeZoneData = await fetchJson<TimeZoneData>(`/${I18n.locale}/complete_sign_up/time_zones`, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  setTimeZoneData({ ...timeZoneData, zones: timeZoneData.zones })
}

export const CompanyForm: React.FC = () => {
  const classes = useStyles()

  const { signUp, setSignUp } = useSignUp()

  const { notifyError } = useNotify()

  const [timeZoneData, setTimeZoneData] = useState<TimeZoneData | null>(null)
  if (!timeZoneData) {
    fetchTimeZoneData(setTimeZoneData)
    return <Loading />
  }

  const handleSubmit = async (values: CompanyFormProps, { setSubmitting }: FormikHelpers<CompanyFormProps>) => {
    try {
      setSubmitting(true)
      const freshSignUp: SignUp = await fetchJson<SignUp>(`/${I18n.locale}/complete_sign_up/save_company`, {
        method: 'PATCH',
        bodyHash: {
          ...values,
          lawFirmVatRegistrationNumber: values.lawFirmIsVatPayer ? values.lawFirmVatRegistrationNumber : null,
          lawFirmCountry: values.lawFirmCountry?.id,
          lawFirmSize: values.lawFirmSize?.id,
          timeZoneName: values.timeZoneName?.id,
          requestedSubdomain: values.subdomain // reserved param name in Rails
        },
        headers: {
          'Content-Type': 'application/json'
        }
      })
      setSubmitting(false)
      setSignUp(freshSignUp)
    } catch (error) {
      setSubmitting(false)
      let message: string | undefined
      if (error?.isValidationError) {
        const freshSignUp: SignUp = await error?.response.json()
        setSignUp(freshSignUp)
        message = freshSignUp.errors
      } else {
        message = ''
      }
      notifyError({ message })
    }
  }

  const initialValues: CompanyFormProps = {
    subdomain: signUp.subdomain ?? '',
    lawFirmName: signUp.lawFirmName ?? '',
    lawFirmRegistrationNumber: signUp.lawFirmRegistrationNumber ?? '',
    lawFirmIsVatPayer: Boolean(signUp.lawFirmVatRegistrationNumber),
    lawFirmVatRegistrationNumber: signUp.lawFirmVatRegistrationNumber ?? '',
    lawFirmCountry: findSelectItemInCodifier(COUNTRIES, signUp.lawFirmCountry ?? 'LV'),
    lawFirmAddress: signUp.lawFirmAddress ?? '',
    lawFirmPostalCode: signUp.lawFirmPostalCode ?? '',
    roleInLawFirm: signUp.roleInLawFirm ?? '',
    lawFirmSize: findSelectItemInCodifier(LAW_FIRM_SIZES, signUp.lawFirmSize),
    practiceArea: signUp.practiceArea ?? '',
    systemLocale: signUp.systemLocale || I18n.locale,
    timeZoneName: findSelectItemInCodifier(timeZoneData.zones, signUp.timeZoneName ?? timeZoneData.default)
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ isSubmitting, setFieldValue, values }) => (
        <Form noValidate>
          <div className={classes.tooltipContainer}>
            <Tooltip title={t('sign_up.company_form.subdomain_tooltip')} className={classes.tooltip}>
              <IconButton size='small'>
                <HelpOutlineIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </div>
          <TextField
            name='subdomain'
            required
            label={t('activerecord.attributes.sign_up.subdomain')}
            placeholder={t('sign_up.company_form.subdomain_placeholder')}
            className={clsx(classes.input, classes.textRight)}
            InputProps={{
              onBlur: (event) => {
                setFieldValue('subdomain', formatSubdomain(event.target.value))
              },
              endAdornment: <>.{window.location.host}/</>
            }}
          />
          <TextField
            name='lawFirmName'
            required
            label={t('activerecord.attributes.sign_up.law_firm_name')}
            placeholder={t('sign_up.company_form.law_firm_name_placeholder')}
            className={classes.input}
          />
          <TextField
            name='lawFirmRegistrationNumber'
            label={t('activerecord.attributes.sign_up.law_firm_registration_number')}
            placeholder={t('sign_up.company_form.law_firm_registration_number_placeholder')}
            className={classes.input}
          />
          <div className={classes.checkbox}>
            <CheckboxField
              name='lawFirmIsVatPayer'
              label={t('activerecord.attributes.sign_up.law_firm_is_vat_payer')}
              CheckboxProps={{
                // checked: values.lawFirmIsVatPayer,
                onChange: (event) => setFieldValue('lawFirmIsVatPayer', event.target.checked)
              }}
            />
          </div>
          {values.lawFirmIsVatPayer && (
            <TextField
              name='lawFirmVatRegistrationNumber'
              required
              label={t('activerecord.attributes.sign_up.law_firm_vat_registration_number')}
              placeholder={t('sign_up.company_form.law_firm_vat_registration_number_placeholder')}
              className={classes.input}
            />
          )}
          <TranslatedItemSelectField
            name='lawFirmCountry'
            required
            label={t('activerecord.attributes.sign_up.law_firm_country')}
            items={COUNTRIES}
            AutocompleteProps={{
              disableClearable: true,
              className: classes.input
            }}
          />
          <TextField
            name='lawFirmAddress'
            required
            label={t('activerecord.attributes.sign_up.law_firm_address')}
            placeholder={t('sign_up.company_form.law_firm_address_placeholder')}
            className={classes.input}
          />
          <TextField
            name='lawFirmPostalCode'
            required
            label={t('activerecord.attributes.sign_up.law_firm_postal_code')}
            placeholder={t('sign_up.company_form.law_firm_postal_code_placeholder')}
            className={classes.input}
          />
          <TextField
            name='roleInLawFirm'
            label={t('activerecord.attributes.sign_up.role_in_law_firm')}
            placeholder={t('sign_up.company_form.role_in_law_firm_placeholder')}
            className={classes.input}
          />
          <TranslatedItemSelectField
            name='lawFirmSize'
            label={t('activerecord.attributes.sign_up.law_firm_size')}
            items={LAW_FIRM_SIZES}
            AutocompleteProps={{
              disableClearable: true,
              className: classes.input
            }}
          />
          <TextField
            name='practiceArea'
            label={t('activerecord.attributes.sign_up.practice_area')}
            placeholder={t('sign_up.company_form.practice_area_placeholder')}
            className={classes.input}
          />
          <TranslatedSelectField
            name='systemLocale'
            required
            label={t('activerecord.attributes.sign_up.system_locale')}
            options={languages}
            translationScope='languages'
            AutocompleteProps={{
              disableClearable: true,
              className: classes.input
            }}
          />
          <TranslatedItemSelectField
            name='timeZoneName'
            label={t('activerecord.attributes.sign_up.time_zone_name')}
            items={timeZoneData.zones}
            AutocompleteProps={{
              className: classes.input
            }}
          />
          <div className={classes.buttons}>
            <Button type='submit' variant='contained' color='primary' className={classes.submit} disabled={isSubmitting} size='large'>
              {t('sign_up.next')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
